import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import { cn } from '@/lib/utils';
import { useRecordReceipt } from '@/hooks/queries/useFinanceReports';
import {
  filingDestinationPhrase,
  usePortalUploadModel,
} from '@/hooks/finance/usePortalUploadModel';
import { formatDate } from '../utils';
import {
  campaignLocalInputToUtcIso,
  campaignTimeZoneId,
  utcIsoToCampaignLocalInput,
} from '@/lib/campaignDateTimeInput';

interface ReceiptEditorProps {
  reportId: string;
  receiptId: string | null;
  receiptAt: string | null;
  /** The report's token, echoed so a stale edit 409s instead of overwriting a concurrent change. */
  concurrencyVersion: number;
}

/**
 * FIN-011 — inline edit affordance for the state-issued receipt. Treasurer
 * uploads to the FCPA / COMET / FL portal, copies the portal-issued ID and
 * timestamp back here. Empty string + null clears both fields.
 */
export function ReceiptEditor({ reportId, receiptId, receiptAt, concurrencyVersion }: ReceiptEditorProps) {
  const { recordReceipt } = useRecordReceipt();
  const portalModel = usePortalUploadModel();
  // The receipt ID is the portal-issued confirmation number. Name the campaign's
  // OWN filing destination (AL → "the Alabama FCPA portal") instead of a hardcoded
  // "FCPA" example — this editor mounts ungated for federal filers too, who have no
  // portal, so they get a jurisdiction-neutral placeholder.
  const receiptIdPlaceholder = portalModel.isPortalUpload
    ? `e.g. the confirmation number from ${filingDestinationPhrase(portalModel)}`
    : 'e.g. your filing confirmation number';
  const [editing, setEditing] = useState(false);
  const [draftId, setDraftId] = useState(receiptId ?? '');
  // HTML datetime-local input wants `YYYY-MM-DDTHH:mm`; backend stores UTC. The wall clock is the
  // CAMPAIGN's (root CLAUDE.md "Campaign Timezone"), both when pre-filling and when saving.
  const toInput = (iso: string | null) => (iso ? utcIsoToCampaignLocalInput(iso, campaignTimeZoneId()) : '');
  const [draftAt, setDraftAt] = useState(toInput(receiptAt));
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Atomic contract (backend rejects 400 on a half-pair): both populated
  // means recording, both empty means clearing. Compute it up-front so the
  // Save button can disable rather than let the request fail.
  const trimmedId = draftId.trim();
  const trimmedAt = draftAt.trim();
  const isClearing = trimmedId === '' && trimmedAt === '';
  const isRecording = trimmedId !== '' && trimmedAt !== '';
  const isHalfPair = !isClearing && !isRecording;

  const handleSave = async () => {
    setErrorMessage(null);
    if (isHalfPair) {
      setErrorMessage(
        'Enter both a receipt ID and a received-at timestamp, or clear both to remove the receipt.',
      );
      return;
    }
    setSaving(true);
    try {
      await recordReceipt({
        reportId,
        data: {
          stateFilingReceiptId: isClearing ? '' : trimmedId,
          stateFilingReceiptAt: isClearing ? null : campaignLocalInputToUtcIso(trimmedAt, campaignTimeZoneId()),
          concurrencyVersion,
        },
      });
      setEditing(false);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'Failed to record receipt');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraftId(receiptId ?? '');
    setDraftAt(toInput(receiptAt));
    setErrorMessage(null);
    setEditing(false);
  };

  const inputClass = cn(
    'rounded-md border border-input bg-background px-3 py-2 text-sm',
    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  );

  if (!editing) {
    return (
      <div className="flex items-center gap-2 text-sm" data-testid="receipt-display">
        {receiptId ? (
          <>
            <span className="font-mono text-foreground">{receiptId}</span>
            {receiptAt && (
              <span className="text-muted-foreground">
                · Received {formatDate(receiptAt.slice(0, 10))}
              </span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">No state receipt recorded yet.</span>
        )}
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid="receipt-edit-button"
          className="ml-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <Pencil className="h-3 w-3" />
          {receiptId ? 'Edit' : 'Add receipt'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="receipt-editor">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label htmlFor="receipt-id-input" className="text-xs font-medium text-muted-foreground">
            State filing receipt ID
          </label>
          <input
            id="receipt-id-input"
            data-testid="receipt-id-input"
            type="text"
            value={draftId}
            onChange={(e) => setDraftId(e.target.value)}
            placeholder={receiptIdPlaceholder}
            className={cn(inputClass, 'mt-1 w-full')}
          />
        </div>
        <div>
          <label htmlFor="receipt-at-input" className="text-xs font-medium text-muted-foreground">
            Received at
          </label>
          <input
            id="receipt-at-input"
            data-testid="receipt-at-input"
            type="datetime-local"
            value={draftAt}
            onChange={(e) => setDraftAt(e.target.value)}
            className={cn(inputClass, 'mt-1')}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleSave} disabled={saving || isHalfPair} data-testid="receipt-save-button">
          {saving ? <InlineSpinner label="Saving" className="mr-1 h-3.5 w-3.5" /> : <Check className="mr-1 h-3.5 w-3.5" />}
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={handleCancel} disabled={saving}>
          <X className="mr-1 h-3.5 w-3.5" />
          Cancel
        </Button>
        {errorMessage && (
          <span className="text-sm text-destructive" data-testid="receipt-error">
            {errorMessage}
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Clearing both fields removes the receipt entirely.
      </p>
    </div>
  );
}
